/**
 * TransitionScreenModal Component
 *
 * Full-screen modal that provides a psychological transition ritual
 * between Pre-Trading and Live Trading phases. Displays a calming
 * screen with an optional motivational quote.
 *
 * Features:
 * - Random quote selection (stable during transition)
 * - Subtle progress bar with smooth CSS animation
 * - Optional auto-advance when timer completes
 * - Always-enabled "Enter Live Trading" button for early exit
 * - Non-dismissible (no backdrop click, Escape, or close button)
 */

import React, { useEffect, useMemo, useRef } from 'react';

import {
  Modal,
  ModalContent,
  ModalBody,
  Button,
} from '@enfomentis/ui';
import { sessionDraftService } from '@/services/SessionDraftService';
import { useCountdown } from '@/hooks/useCountdown';
import type { TransitionQuote } from '@/types/schema/PreTradingSettings';

// ============================================================================
// Types
// ============================================================================

export interface TransitionScreenModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when transition completes (auto-advance or button click) */
  onClose: () => void;
  /** Countdown duration in seconds */
  transitionDuration: number;
  /** Whether to auto-advance when countdown reaches 0 */
  autoAdvance: boolean;
  /** Array of quotes to randomly select from */
  transitionQuotes: TransitionQuote[];
  /** Session draft ID for recording modal usage */
  draftId: string;
}

// ============================================================================
// Component
// ============================================================================

export const TransitionScreenModal: React.FC<TransitionScreenModalProps> = ({
  isOpen,
  onClose,
  transitionDuration,
  autoAdvance,
  transitionQuotes,
  draftId,
}) => {
  // Track if we've already recorded usage for this opening
  const hasRecordedUsageRef = useRef(false);

  // Track if countdown has actually started (to prevent race condition with auto-advance)
  // Without this, auto-advance fires immediately because countdown is 0 before useCountdown's effect runs
  const hasCountdownStartedRef = useRef(false);

  // Select a random quote when modal opens (stable during countdown)
  // Only compute when open and quotes exist to avoid unnecessary work
  const selectedQuote = useMemo(() => {
    if (!isOpen || transitionQuotes.length === 0) {
      return null;
    }
    const randomIndex = Math.floor(Math.random() * transitionQuotes.length);
    return transitionQuotes[randomIndex];
  }, [isOpen, transitionQuotes.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Countdown timer - resets to 0 when modal closes
  const countdown = useCountdown(isOpen ? transitionDuration : 0);

  // Record transitionScreenUsed when modal opens (fire-and-forget)
  useEffect(() => {
    if (isOpen && draftId && !hasRecordedUsageRef.current) {
      hasRecordedUsageRef.current = true;
      sessionDraftService.updateTransitionScreenUsed(draftId).catch((error) => {
        // Log but don't block - this is fire-and-forget
        console.error('Failed to record transition screen usage:', error);
      });
    }

    // Reset the ref when modal closes so we can record again on next open
    if (!isOpen) {
      hasRecordedUsageRef.current = false;
    }
  }, [isOpen, draftId]);

  // Track when countdown actually starts (countdown > 0 means useCountdown has initialized)
  useEffect(() => {
    if (isOpen && countdown > 0) {
      hasCountdownStartedRef.current = true;
    }
    // Reset when modal closes
    if (!isOpen) {
      hasCountdownStartedRef.current = false;
    }
  }, [isOpen, countdown]);

  // Auto-advance when countdown reaches 0 (if enabled)
  // Guard: only advance if countdown actually started (prevents race condition on first render)
  useEffect(() => {
    if (isOpen && countdown === 0 && autoAdvance && hasCountdownStartedRef.current) {
      onClose();
    }
  }, [isOpen, countdown, autoAdvance, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={() => {}} // Prevent external closing
      isDismissable={false}
      hideCloseButton={true}
      isKeyboardDismissDisabled={true}
      size="full"
      classNames={{
        body: 'p-0',
      }}
    >
      <ModalContent>
        <ModalBody className="flex flex-col items-center justify-center min-h-screen p-8">
          {/* Quote Display */}
          {selectedQuote && (
            <div className="max-w-2xl text-center mb-16">
              <blockquote className="text-2xl md:text-3xl font-light text-text-primary leading-relaxed italic">
                {selectedQuote.text}
              </blockquote>
              {selectedQuote.author && (
                <cite className="block mt-4 text-lg text-text-secondary not-italic">
                  — {selectedQuote.author}
                </cite>
              )}
            </div>
          )}

          {/* Enter Live Trading Button - Always Enabled */}
          <Button
            color="primary"
            size="sm"
            variant="flat"
            onPress={onClose}
            className="min-w-48"
          >
            Enter Live Trading
          </Button>

          {/* Subtle progress bar - smooth CSS animation */}
          {isOpen && transitionDuration > 0 && (
            <div className="w-48 h-1 mt-6 bg-primary-500/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500/50 rounded-full"
                style={{
                  animation: `fillProgress ${transitionDuration}s linear forwards`,
                }}
              />
              <style>{`
                @keyframes fillProgress {
                  from { width: 0%; }
                  to { width: 100%; }
                }
              `}</style>
            </div>
          )}

          {/* Manual click hint when auto-advance is disabled and timer done */}
          {!autoAdvance && countdown === 0 && (
            <p className="text-text-secondary text-sm mt-6">
              Click the button when you're ready
            </p>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
